<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import AppIcon from './AppIcon.vue'
import BluffReveal from './BluffReveal.vue'
import type { HostBluffView } from '../../shared/contracts/bluff'
import { ApiError, apiRequest } from '../services/api'
import { optionLetter, secondsLeft, useClock } from '../services/bluff'

const props = defineProps<{ sessionId: string; bluff: HostBluffView; prompt: string | null; closeAt: string | null }>()
const emit = defineEmits<{ changed: [] }>()

const error = ref('')
const busy = ref(false)
const decoyText = ref('')
const selectedGroup = ref('')
const rules = ref({ ...props.bluff.rules })
const now = useClock()
const remaining = computed(() => secondsLeft(props.closeAt, now.value))
const accepted = computed(() => props.bluff.submissions.filter((item) => item.status === 'ACCEPTED'))
const rejected = computed(() => props.bluff.submissions.filter((item) => item.status === 'REJECTED'))
const unusedPrepared = computed(() => (props.bluff.truth?.preparedDecoys ?? [])
  .filter((text) => ![...props.bluff.decoys.map((decoy) => decoy.text), ...props.bluff.groups.map((group) => group.label)]
    .some((used) => used.trim().toLowerCase() === text.trim().toLowerCase())))
const progress = computed(() => props.bluff.phase === 'VOTE_OPEN' ? props.bluff.voted : props.bluff.submitted)

watch(() => props.bluff.rules, (value) => { rules.value = { ...value } })

async function send(path: string, body: Record<string, unknown>, confirmation?: string): Promise<void> {
  if (confirmation && !window.confirm(confirmation)) return
  error.value = ''
  busy.value = true
  try {
    await apiRequest(`/api/admin/sessions/${props.sessionId}/${path}`, { method: 'POST', body: JSON.stringify(body) })
    emit('changed')
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'The host action failed.'
  } finally { busy.value = false }
}

const act = (action: string, confirmation?: string) => send('action', { action }, confirmation)
const moderate = (body: Record<string, unknown>) => send('bluff', body)

async function addDecoy(text: string, prepared: boolean): Promise<void> {
  await moderate({ action: 'ADD_DECOY', text, prepared })
  if (!prepared && !error.value) decoyText.value = ''
}

async function mergeInto(targetGroupKey: string): Promise<void> {
  if (!selectedGroup.value) return
  await moderate({ action: 'MERGE_BLUFFS', sourceGroupKey: selectedGroup.value, targetGroupKey })
  selectedGroup.value = ''
}

function timerValue(value: string): number | null {
  return value === '' ? null : Number(value)
}
</script>

<template>
  <div class="stack stack--lg">
    <p
      v-if="error"
      class="alert"
      role="alert"
    >
      {{ error }}
    </p>

    <!-- lobby rules -->
    <section
      v-if="bluff.phase === 'LOBBY'"
      v-reveal
      class="panel panel--dark stack"
    >
      <span class="sub-title"><strong>Game</strong> setup</span>
      <h2 class="text-xxl fw-6">
        {{ bluff.packName }} · {{ bluff.questionCount }} questions
      </h2>
      <p class="muted-text">
        Rules lock when the first question opens. Leave a timer empty to advance by hand.
      </p>
      <div class="score-grid">
        <label>Bluff timer (seconds)<span class="input-single input-single--soft"><input
          :value="rules.bluffSeconds ?? ''"
          type="number"
          min="5"
          max="600"
          @input="rules.bluffSeconds = timerValue(($event.target as HTMLInputElement).value)"
        ></span></label>
        <label>Vote timer (seconds)<span class="input-single input-single--soft"><input
          :value="rules.voteSeconds ?? ''"
          type="number"
          min="5"
          max="600"
          @input="rules.voteSeconds = timerValue(($event.target as HTMLInputElement).value)"
        ></span></label>
        <label>Points for the truth<span class="input-single input-single--soft"><input
          v-model.number="rules.truthPoints"
          type="number"
          min="0"
          max="10000"
        ></span></label>
        <label>Points per team fooled<span class="input-single input-single--soft"><input
          v-model.number="rules.foolPoints"
          type="number"
          min="0"
          max="10000"
        ></span></label>
      </div>
      <div class="button-row">
        <button
          type="button"
          class="btn--secondary"
          :disabled="busy"
          @click="moderate({ action: 'UPDATE_RULES', rules })"
        >
          <AppIcon name="settings" /> Save rules
        </button>
      </div>
    </section>

    <!-- current question, host eyes only -->
    <section
      v-if="prompt && bluff.truth"
      v-reveal
      class="panel panel--dark stack"
    >
      <div class="row-flex row-flex--between">
        <span class="sub-title"><strong>Question</strong> {{ bluff.questionNumber }} of {{ bluff.questionCount }}</span>
        <span
          v-if="remaining !== null && ['BLUFF_OPEN', 'VOTE_OPEN'].includes(bluff.phase)"
          class="bluff-countdown"
        ><AppIcon
          name="clock"
          :size="18"
        />{{ remaining }}s</span>
      </div>
      <h2 class="text-xxl fw-6">
        {{ prompt }}
      </h2>
      <div class="truth-card">
        <span class="text-sm fw-6 upper">Host only · the truth</span>
        <strong>{{ bluff.truth.truth }}</strong>
        <p
          v-if="bluff.truth.aliases.length"
          class="text-sm"
        >
          Also accepted as the truth: {{ bluff.truth.aliases.join(', ') }}
        </p>
        <p class="text-sm muted-text">
          {{ bluff.truth.explanation }} ({{ bluff.truth.sourceName }})
        </p>
      </div>
      <div
        v-if="['BLUFF_OPEN', 'REVIEW', 'VOTE_OPEN'].includes(bluff.phase)"
        class="row-flex"
      >
        <div
          class="progress-bar"
          style="flex: 1 1 12rem"
        >
          <div
            class="progress-bar-percent"
            :style="{ width: `${bluff.eligible ? Math.round((progress / bluff.eligible) * 100) : 0}%` }"
          />
        </div>
        <p class="fw-6">
          {{ progress }} of {{ bluff.eligible }} teams {{ bluff.phase === 'VOTE_OPEN' ? 'voted' : 'bluffed' }}
        </p>
      </div>
      <p
        v-if="bluff.missingTeams.length"
        class="text-sm muted-text"
      >
        Waiting on: {{ bluff.missingTeams.join(', ') }}
      </p>
      <p
        v-if="bluff.votingClosed"
        class="notice"
      >
        The vote timer has run out. Reveal when the room is ready.
      </p>
    </section>

    <!-- controls -->
    <nav
      v-reveal
      class="panel host-controls"
      aria-label="Live game controls"
    >
      <button
        v-if="bluff.phase === 'LOBBY' || bluff.phase === 'STANDINGS'"
        type="button"
        class="btn--primary"
        :disabled="busy || bluff.questionNumber >= bluff.questionCount && bluff.phase === 'STANDINGS'"
        @click="act('OPEN_BLUFFS')"
      >
        <AppIcon name="play" /> {{ bluff.phase === 'LOBBY' ? 'Open first question' : 'Next question' }}
      </button>
      <button
        v-if="bluff.phase === 'BLUFF_OPEN'"
        type="button"
        class="btn--primary"
        :disabled="busy"
        @click="act('CLOSE_BLUFFS')"
      >
        <AppIcon name="lock" /> Close bluffs and review
      </button>
      <button
        v-if="bluff.phase === 'REVIEW'"
        type="button"
        class="btn--primary"
        :disabled="busy"
        @click="act('OPEN_VOTING')"
      >
        <AppIcon name="eye" /> Open voting
      </button>
      <button
        v-if="bluff.phase === 'REVIEW'"
        type="button"
        class="btn--secondary"
        :disabled="busy"
        @click="act('REOPEN_BLUFFS')"
      >
        <AppIcon name="lock-open" /> Reopen bluffs
      </button>
      <button
        v-if="bluff.phase === 'VOTE_OPEN'"
        type="button"
        class="btn--primary"
        :disabled="busy"
        @click="act('REVEAL')"
      >
        <AppIcon name="sparkles" /> Reveal and score
      </button>
      <button
        v-if="bluff.phase === 'REVEAL'"
        type="button"
        class="btn--primary"
        :disabled="busy"
        @click="act('SHOW_STANDINGS')"
      >
        <AppIcon name="trophy" /> Show standings
      </button>
      <button
        v-if="bluff.phase === 'STANDINGS'"
        type="button"
        class="btn--secondary"
        :disabled="busy"
        @click="act('COMPLETE', bluff.questionNumber < bluff.questionCount ? 'Finish now? The remaining questions will not be played.' : undefined)"
      >
        <AppIcon name="flag" /> Finish game
      </button>
      <button
        v-if="['BLUFF_OPEN', 'REVIEW', 'VOTE_OPEN'].includes(bluff.phase)"
        type="button"
        class="btn--danger"
        :disabled="busy"
        @click="act('SKIP_QUESTION', 'Skip this question without awarding points?')"
      >
        <AppIcon name="skip" /> Skip question
      </button>
      <button
        v-if="!['COMPLETE', 'ENDED'].includes(bluff.phase)"
        type="button"
        class="btn--danger"
        :disabled="busy"
        @click="act('END', 'End this live session early? Players will no longer be able to continue.')"
      >
        <AppIcon name="x" /> End session early
      </button>
    </nav>

    <!-- moderation -->
    <section
      v-if="['BLUFF_OPEN', 'REVIEW'].includes(bluff.phase)"
      v-reveal
      class="panel panel--dark stack"
    >
      <div>
        <h2 class="text-xxl fw-6">
          {{ bluff.phase === 'REVIEW' ? 'Review bluffs' : 'Incoming bluffs' }}
        </h2>
        <p class="muted-text">
          Matching bluffs are merged automatically. Reject anything unsuitable (a rejected team can retry while bluffing is open),
          merge near-duplicates, and add decoys. Voting needs at least three answers; unused prepared decoys fill any gap.
        </p>
      </div>
      <p
        v-if="!bluff.groups.length"
        class="muted-text"
      >
        No bluffs yet.
      </p>
      <article
        v-for="suggestion in bluff.suggestions"
        :key="`${suggestion.sourceKey}-${suggestion.targetKey}`"
        class="list-card suggestion-card"
      >
        <p>Similar: <strong>“{{ suggestion.sourceLabel }}”</strong> and <strong>“{{ suggestion.targetLabel }}”</strong></p>
        <div>
          <button
            type="button"
            class="btn--secondary btn--sm"
            :disabled="busy"
            @click="moderate({ action: 'MERGE_BLUFFS', sourceGroupKey: suggestion.sourceKey, targetGroupKey: suggestion.targetKey })"
          >
            Merge into one answer
          </button>
        </div>
      </article>
      <article
        v-for="group in bluff.groups"
        :key="group.key"
        class="list-card group-card"
      >
        <label class="check-row"><input
          v-model="selectedGroup"
          type="radio"
          :value="group.key"
        > <strong>{{ group.label }}</strong></label>
        <span class="muted-text">{{ group.teams.join(', ') }}</span>
        <div class="member-row">
          <template
            v-for="submission in accepted.filter((item) => item.groupKey === group.key)"
            :key="submission.id"
          >
            <span
              v-if="submission.nearTruth"
              class="tag tag--secondary"
            >close to the truth</span>
            <button
              type="button"
              class="btn--danger btn--sm"
              :disabled="busy"
              @click="moderate({ action: 'REJECT_BLUFF', submissionId: submission.id })"
            >
              Reject {{ submission.teamName }}’s “{{ submission.text }}”
            </button>
            <button
              v-if="group.submissionIds.length > 1"
              type="button"
              class="btn--ghost btn--sm"
              :disabled="busy"
              @click="moderate({ action: 'SPLIT_BLUFF', submissionId: submission.id })"
            >
              Separate {{ submission.teamName }}
            </button>
          </template>
        </div>
        <div v-if="selectedGroup && selectedGroup !== group.key">
          <button
            type="button"
            class="btn--primary btn--sm"
            :disabled="busy"
            @click="mergeInto(group.key)"
          >
            Merge selected here
          </button>
        </div>
      </article>
      <template v-if="rejected.length">
        <h3 class="text-xl fw-6">
          Rejected
        </h3>
        <article
          v-for="submission in rejected"
          :key="submission.id"
          class="list-card muted"
        >
          <span>{{ submission.teamName }}: “{{ submission.text }}”</span>
          <div>
            <button
              type="button"
              class="btn--ghost btn--sm"
              :disabled="busy"
              @click="moderate({ action: 'RESTORE_BLUFF', submissionId: submission.id })"
            >
              Restore
            </button>
          </div>
        </article>
      </template>

      <h3 class="text-xl fw-6">
        Decoys
      </h3>
      <p class="text-sm muted-text">
        Ballot preview: {{ bluff.optionCountPreview }} answers including the truth.
        <template v-if="bluff.optionCountPreview < 3">
          {{ unusedPrepared.length ? 'Prepared decoys will be added automatically.' : 'Add a decoy before opening voting.' }}
        </template>
      </p>
      <div class="button-row">
        <span
          v-for="decoy in bluff.decoys"
          :key="decoy.id"
          class="tag tag--muted"
        >{{ decoy.text }} <button
          type="button"
          class="text-button"
          :aria-label="`Remove decoy ${decoy.text}`"
          :disabled="busy"
          @click="moderate({ action: 'REMOVE_DECOY', decoyId: decoy.id })"
        ><AppIcon
          name="x"
          :size="14"
        /></button></span>
      </div>
      <div class="button-row">
        <button
          v-for="text in unusedPrepared"
          :key="text"
          type="button"
          class="btn--ghost btn--sm"
          :disabled="busy"
          @click="addDecoy(text, true)"
        >
          <AppIcon
            name="plus"
            :size="16"
          /> {{ text }}
        </button>
      </div>
      <form
        class="inline-form"
        @submit.prevent="addDecoy(decoyText, false)"
      >
        <div class="input-single input-single--soft">
          <AppIcon name="pencil" />
          <input
            v-model="decoyText"
            maxlength="80"
            aria-label="Custom decoy"
            placeholder="Write your own decoy"
          >
        </div>
        <button
          type="submit"
          class="btn--secondary btn--sm"
          :disabled="busy || !decoyText.trim()"
        >
          Add decoy
        </button>
      </form>
    </section>

    <!-- frozen ballot -->
    <section
      v-if="bluff.phase === 'VOTE_OPEN'"
      v-reveal
      class="panel panel--dark stack"
    >
      <h2 class="text-xxl fw-6">
        Ballot
      </h2>
      <ul class="option-list">
        <li
          v-for="(option, index) in bluff.options"
          :key="option.id"
          class="reveal-option"
          :class="{ 'is-truth': option.kind === 'TRUTH' }"
        >
          <div class="reveal-option__head">
            <strong><span class="option-letter">{{ optionLetter(index) }}</span>{{ option.text }}</strong>
            <span class="tag">{{ option.votes }} {{ option.votes === 1 ? 'vote' : 'votes' }}</span>
          </div>
          <p class="text-sm muted-text">
            {{ option.kind === 'TRUTH' ? 'The truth' : option.kind === 'DECOY' ? 'Decoy' : `Bluff by ${option.authors.join(', ')}` }}
          </p>
        </li>
      </ul>
    </section>

    <section
      v-if="bluff.reveal && ['REVEAL', 'STANDINGS', 'COMPLETE'].includes(bluff.phase)"
      v-reveal
      class="panel panel--dark stack"
    >
      <h2 class="text-xxl fw-6">
        Reveal
      </h2>
      <BluffReveal :reveal="bluff.reveal" />
    </section>
  </div>
</template>
